import {
  ApiError,
  containersControllerArchiveContainer,
  containersControllerArchiveOption,
  containersControllerCreateContainer,
  containersControllerCreateContainerOption,
  containersControllerCreatePartnerOwnedContainer,
  containersControllerCreatePartnerOwnedContainerOption,
  containersControllerDeleteContainer,
  containersControllerDeleteOption,
  containersControllerGetContainer,
  containersControllerListContainers,
  containersControllerPrecheckArchiveContainer,
  containersControllerPrecheckArchiveOption,
  containersControllerRestoreOption,
  containersControllerToggleContainerOptionProductType,
  containersControllerToggleContainerProductType,
  containersControllerUpdateContainer,
  containersControllerUpdateContainerOption,
  type UpdateContainerOptionDto,
} from '@artic/api-client';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { expectData } from '../_shared/api';
import { PRODUCTION_PLANNER_KEY } from '../production-planner/queries';
import { PRODUCTS_KEY } from '../products/queries';
import type {
  ArchiveCompletionSummary,
  ArchiveDecisionAction,
  ArchiveOptionResult,
  ContainerArchivePrecheck,
  ContainerDetail,
  ContainerListItem,
  OptionArchivePrecheck,
} from './types';

export const CONTAINERS_KEY = 'containers' as const;

/**
 * Breadcrumb resolver helper: the detail queryKey carries the partner scope
 * as its fourth element, which route matches don't know — prefix-match the
 * containerId and take whichever scoped entry is populated.
 */
export function getCachedContainerName(
  queryClient: QueryClient,
  containerId: string,
): string | null {
  const entries = queryClient.getQueriesData<ContainerDetail>({
    queryKey: [CONTAINERS_KEY, 'detail', containerId],
  });
  for (const [, data] of entries) {
    if (data?.container_name) return data.container_name;
  }
  return null;
}

// ─── List ─────────────────────────────────────────────────────────
//
// Unscoped (admin Container Library) lists everything; with a partnerId
// (Settings > Columns > DfMA Containers) the backend returns standard
// containers plus that partner's customs only, with option counts scoped
// the same way. Non-admin callers must pass partnerId — the backend 400s
// otherwise.

export function containerListQueryOptions(partnerId?: string) {
  return queryOptions({
    queryKey: [CONTAINERS_KEY, 'list', partnerId ?? 'all'] as const,
    queryFn: async ({ signal }) => {
      const res = await containersControllerListContainers(
        partnerId ? { partner_id: partnerId } : undefined,
        { signal },
      );
      return expectData<ContainerListItem[]>(res);
    },
    enabled: true,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Product-type container ───────────────────────────────────────
//
// The Product Type container drives the "Product Types" toggle section and
// the schedule's product-type dropdown. Its options change rarely, so this
// query has a long staleTime. Resolution is a two-step fetch (list -> detail)
// because there is no single endpoint that returns a container by key.
// Pass a partnerId for partner-context callers (required for non-admins):
// the option list is then scoped to global + that partner's customs.
export function productTypeContainerQueryOptions(partnerId?: string) {
  const params = partnerId ? { partner_id: partnerId } : undefined;
  return queryOptions({
    queryKey: [CONTAINERS_KEY, 'product-type', partnerId ?? 'all'] as const,
    queryFn: async ({ signal }) => {
      const listRes = await containersControllerListContainers(params, { signal });
      const pt = expectData<ContainerListItem[]>(listRes).find(
        (c) => c.container_key === 'product_type',
      );
      if (!pt) throw new Error('product_type container not found');
      const detailRes = await containersControllerGetContainer(pt.container_id, params, { signal });
      return expectData<ContainerDetail>(detailRes);
    },
    enabled: true,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Detail ───────────────────────────────────────────────────────
//
// Same scoping contract as the list: with a partnerId the options are
// filtered to global + that partner's customs, and another partner's
// custom container reads as 404. The queryKey keeps the scope as its
// fourth element so the admin library and the partner view never share
// cached entries; prefix invalidation on [CONTAINERS_KEY] still hits both.

export function containerDetailQueryOptions(containerId: string | undefined, partnerId?: string) {
  return queryOptions({
    queryKey: [CONTAINERS_KEY, 'detail', containerId, partnerId ?? 'all'] as const,
    queryFn: async ({ signal }) => {
      if (!containerId) throw new Error('containerId is required');
      const res = await containersControllerGetContainer(
        containerId,
        partnerId ? { partner_id: partnerId } : undefined,
        { signal },
      );
      return expectData<ContainerDetail>(res);
    },
    enabled: !!containerId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

// ─── Create container (mutation) ──────────────────────────────────

export interface CreateContainerParams {
  container_name: string;
  /**
   * Slug identifier. Optional: when omitted the backend derives it from
   * container_name and retries with numeric suffixes on collision — the
   * partner create sheet relies on this.
   */
  container_key?: string;
  container_description?: string | null;
  container_sort_order?: number;
  is_standard?: boolean;
  /**
   * Owning partner UUID. NULL for standard library; required when
   * `is_standard` is false. The /partner-owned endpoint enforces both.
   */
  owner_partner_id?: string | null;
}

export function useCreateContainer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateContainerParams) => {
      const res = await containersControllerCreateContainer(params);
      return expectData<ContainerDetail>(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CONTAINERS_KEY] });
    },
  });
}

/**
 * Partner-context create: routes through /containers/partner-owned which
 * goes via `assertPartnerAccess` instead of `TrunkAdminGuard`, so partner
 * users can create their own custom containers without the email-domain
 * admin gate. Forces `is_standard=false` and `owner_partner_id` server-side.
 */
export function useCreatePartnerOwnedContainer(partnerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: Omit<CreateContainerParams, 'is_standard' | 'owner_partner_id'>) => {
      // Defence-in-depth: the sheet guards isPartnerOwned, but a stray caller
      // passing '' should fail fast with a clear message instead of POSTing an
      // empty owner_partner_id (which the backend would reject with 400).
      if (!partnerId.trim()) {
        throw new Error('partnerId is required to create a partner-owned container');
      }
      const res = await containersControllerCreatePartnerOwnedContainer({
        ...params,
        is_standard: false,
        owner_partner_id: partnerId,
      });
      return expectData<ContainerDetail>(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CONTAINERS_KEY] });
    },
  });
}

// ─── Update container (mutation) ──────────────────────────────────

export interface UpdateContainerParams {
  container_name?: string;
  container_key?: string;
  container_description?: string | null;
  container_sort_order?: number;
  is_standard?: boolean;
}

/**
 * Typed 409 surface from PATCH /containers/:id so the Edit dialog can
 * pin the error to the offending input instead of a top-level toast.
 */
export class ContainerConflictError extends Error {
  public readonly field: 'container_name' | 'container_key';

  constructor(field: 'container_name' | 'container_key', message: string) {
    super(message);
    this.name = 'ContainerConflictError';
    this.field = field;
  }
}

export function useUpdateContainer(containerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateContainerParams) => {
      try {
        const res = await containersControllerUpdateContainer(containerId, params);
        return expectData<ContainerDetail>(res);
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 409) {
          const body = err.bodyAsRecord;
          const rawField = body?.['field'];
          const rawMessage = body?.['message'];
          const message = typeof rawMessage === 'string' ? rawMessage : err.message;
          if (rawField === 'container_name' || rawField === 'container_key') {
            throw new ContainerConflictError(rawField, message);
          }
        }
        throw err;
      }
    },
    onSuccess: () => {
      // Prefix invalidation refetches the active detail query (scoped or
      // unscoped — the key's scope element isn't known here), the list view,
      // and the product-type cache in one shot.
      void queryClient.invalidateQueries({ queryKey: [CONTAINERS_KEY] });
    },
  });
}

// ─── Create option (mutation) ─────────────────────────────────────

interface CreateOptionParams {
  option_name: string;
  /** Omit to let the backend assign a unique 3-letter code. */
  option_code?: string;
  option_score?: number;
  option_weight?: number;
  option_sort_order?: number;
}

export function useCreateContainerOption(containerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateOptionParams) => {
      const res = await containersControllerCreateContainerOption(containerId, params);
      return expectData<ContainerDetail>(res);
    },
    onSuccess: () => {
      // Broad invalidation under the CONTAINERS_KEY prefix catches list,
      // detail (this container), and the product-type cache if the mutated
      // container happens to be product_type — all without enumerating keys.
      void queryClient.invalidateQueries({ queryKey: [CONTAINERS_KEY] });
    },
  });
}

/**
 * Partner-context create: routes through /containers/:containerId/partner-owned-options
 * which goes via `assertPartnerAccess` instead of `TrunkAdminGuard`. Forces
 * `is_standard=false` and `owner_partner_id` server-side, so the new option
 * belongs to the supplied partner and shows up alongside the standard options
 * on the planner's container dropdown.
 */
export function useCreatePartnerOwnedContainerOption(containerId: string, partnerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateOptionParams) => {
      if (!partnerId.trim()) {
        throw new Error('partnerId is required to create a partner-owned option');
      }
      const res = await containersControllerCreatePartnerOwnedContainerOption(containerId, {
        ...params,
        is_standard: false,
        owner_partner_id: partnerId,
      });
      return expectData<ContainerDetail>(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CONTAINERS_KEY] });
    },
  });
}

// ─── Update option (mutation) ─────────────────────────────────────

interface UpdateOptionParams {
  optionId: string;
  data: UpdateContainerOptionDto;
}

export function useUpdateContainerOption(_containerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ optionId, data }: UpdateOptionParams) => {
      await containersControllerUpdateContainerOption(optionId, data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CONTAINERS_KEY] });
    },
  });
}

// ─── Reorder options (mutation) ───────────────────────────────────
//
// There is no batch endpoint for option ordering, so we fire one PATCH per
// changed row in parallel — mirrors the pattern in
// `routes/products/containers.tsx` for partner-container reorder. The
// optimistic update rewrites the cached detail's options array so the row
// snaps into its new position before the network round-trip resolves.

interface ReorderOptionsArgs {
  /** Container option IDs in the desired final order. */
  optionIds: string[];
}

export function useReorderContainerOptions(containerId: string, partnerId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ optionIds }: ReorderOptionsArgs) => {
      await Promise.all(
        optionIds.map((optionId, index) =>
          containersControllerUpdateContainerOption(optionId, { option_sort_order: index }),
        ),
      );
    },
    onMutate: async ({ optionIds }) => {
      // The scope must match the caller's detail query or the optimistic
      // reorder writes to a key nobody is rendering.
      const { queryKey } = containerDetailQueryOptions(containerId, partnerId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ContainerDetail>(queryKey);
      if (previous) {
        const orderMap = new Map(optionIds.map((id, i) => [id, i]));
        const next: ContainerDetail = {
          ...previous,
          options: previous.options
            .map((o) =>
              orderMap.has(o.container_option_id)
                ? {
                    ...o,
                    option_sort_order: orderMap.get(o.container_option_id) ?? o.option_sort_order,
                  }
                : o,
            )
            .slice()
            .sort((a, b) => a.option_sort_order - b.option_sort_order),
        };
        queryClient.setQueryData(queryKey, next);
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        const { queryKey } = containerDetailQueryOptions(containerId, partnerId);
        queryClient.setQueryData(queryKey, ctx.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [CONTAINERS_KEY] });
    },
  });
}

// ─── Toggle container <-> product-type link (mutation) ─────────────

interface ToggleProductTypeParams {
  product_type_option_id: string;
  enabled: boolean;
}

export function useToggleContainerProductType(containerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ToggleProductTypeParams) => {
      const res = await containersControllerToggleContainerProductType(containerId, params);
      return expectData<ContainerDetail>(res);
    },
    onSuccess: () => {
      // Prefix invalidation refetches the active detail query (whatever its
      // scope), and covers the product_type container's own caches when the
      // toggled container is product_type itself.
      void queryClient.invalidateQueries({ queryKey: [CONTAINERS_KEY] });
    },
  });
}

// ─── Toggle option <-> product-type scoping (ENG-677 mutation) ─────

interface ToggleOptionProductTypeParams {
  optionId: string;
  product_type_option_id: string;
  /** true = green (selectable); false = grey (excluded) for this product type. */
  enabled: boolean;
}

/**
 * ENG-677: toggle whether a single option is green (selectable) for a product
 * type. `containerId` is only used for cache scoping via the shared prefix; the
 * endpoint keys on the option. Returns the full container detail, and a prefix
 * invalidation refetches the active (possibly partner-scoped) detail query.
 */
export function useToggleContainerOptionProductType(_containerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ optionId, ...body }: ToggleOptionProductTypeParams) => {
      const res = await containersControllerToggleContainerOptionProductType(optionId, body);
      return expectData<ContainerDetail>(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CONTAINERS_KEY] });
    },
  });
}

// ─── ENG-501 archive ──────────────────────────────────────────────

/**
 * Archiving rebinds work-order products and flips container visibility, so it
 * touches the container library/detail/config AND the products + production
 * planner caches. Invalidate all three.
 */
function invalidateAfterArchive(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [CONTAINERS_KEY] });
  void queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
  void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY] });
}

/** Per-option work-order usage + which modal to show. Fresh at archive time. */
export function optionArchivePrecheckQueryOptions(optionId: string | undefined) {
  return queryOptions({
    queryKey: [CONTAINERS_KEY, 'option-archive-precheck', optionId] as const,
    queryFn: async ({ signal }) => {
      if (!optionId) throw new Error('optionId is required');
      const res = await containersControllerPrecheckArchiveOption(optionId, { signal });
      return expectData<OptionArchivePrecheck>(res);
    },
    enabled: !!optionId,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

/** Aggregate + per-option pre-check for the full-container archive wizard. */
export function containerArchivePrecheckQueryOptions(containerId: string | undefined) {
  return queryOptions({
    queryKey: [CONTAINERS_KEY, 'container-archive-precheck', containerId] as const,
    queryFn: async ({ signal }) => {
      if (!containerId) throw new Error('containerId is required');
      const res = await containersControllerPrecheckArchiveContainer(containerId, { signal });
      return expectData<ContainerArchivePrecheck>(res);
    },
    enabled: !!containerId,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

export function useArchiveContainerOption(_containerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { optionId: string; mode?: 'archive' | 'archive_from_future' }) => {
      const res = await containersControllerArchiveOption(params.optionId, {
        mode: params.mode ?? 'archive',
      });
      return expectData<ArchiveOptionResult>(res);
    },
    onSuccess: () => invalidateAfterArchive(queryClient),
    onError: () => invalidateAfterArchive(queryClient),
  });
}

export function useRestoreContainerOption(_containerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (optionId: string) => {
      await containersControllerRestoreOption(optionId);
    },
    onSuccess: () => invalidateAfterArchive(queryClient),
    onError: () => invalidateAfterArchive(queryClient),
  });
}

export function useDeleteContainerOption(_containerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (optionId: string) => {
      await containersControllerDeleteOption(optionId);
    },
    onSuccess: () => invalidateAfterArchive(queryClient),
    onError: () => invalidateAfterArchive(queryClient),
  });
}

export function useArchiveContainer(containerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      decisions: { container_option_id: string; action: ArchiveDecisionAction }[],
    ) => {
      const res = await containersControllerArchiveContainer(containerId, { decisions });
      return expectData<ArchiveCompletionSummary>(res);
    },
    onSuccess: () => invalidateAfterArchive(queryClient),
  });
}

/**
 * ENG-634 — hard-delete an empty container (no options). Like archive, it can
 * drop the container from the planner/products caches, so reuse the same broad
 * invalidation.
 */
export function useDeleteContainer(containerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await containersControllerDeleteContainer(containerId);
    },
    onSuccess: () => invalidateAfterArchive(queryClient),
    onError: () => invalidateAfterArchive(queryClient),
  });
}
